import React from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  RefreshCw,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Github,
  Globe,
  User,
  Briefcase,
  GraduationCap,
  Award,
  Code,
  Languages,
  Heart,
} from "lucide-react";
import { motion } from "framer-motion";

interface ResumeCardProps {
  readonly details: {
    readonly name?: string;
    readonly email?: string;
    readonly phone?: string;
    readonly location?: string;
    readonly linkedin?: string;
    readonly github?: string;
    readonly portfolio?: string;
    readonly summary?: string;
    readonly skills?: readonly string[];
    readonly technicalSkills?: readonly string[];
    readonly softSkills?: readonly string[];
    readonly experience?: readonly {
      readonly company?: string;
      readonly role?: string;
      readonly dates?: string;
      readonly description?: string;
      readonly location?: string;
      readonly duration?: string;
      readonly achievements?: readonly string[];
      readonly technologies?: readonly string[];
    }[];
    readonly education?: readonly {
      readonly institution?: string;
      readonly degree?: string;
      readonly graduationYear?: string;
      readonly gpa?: string;
      readonly honors?: string;
      readonly relevantCourses?: readonly string[];
    }[];
    readonly certifications?: readonly {
      readonly name?: string;
      readonly issuer?: string;
      readonly date?: string;
      readonly credentialId?: string;
    }[];
    readonly projects?: readonly {
      readonly name?: string;
      readonly description?: string;
      readonly technologies?: readonly string[];
      readonly url?: string;
      readonly duration?: string;
    }[];
    readonly languages?: readonly string[];
    readonly interests?: readonly string[];
  };
  readonly onReparse: () => void;
}

const ResumeCard: React.FC<Readonly<ResumeCardProps>> = ({
  details,
  onReparse,
}) => {

  /* =======================
     🔧 HELPERS (UI SAFE)
     ======================= */
  const isValid = (v?: string) =>
    v && v !== "0000" && v.trim();

  const hasValidArray = <T,>(
    arr: readonly T[] | undefined,
    check: (item: T) => boolean
  ) => Array.isArray(arr) && arr.some(check);

  /* ======================= */

  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
    exit: { opacity: 0, y: 30 },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="w-full"
    >
      <Card className="professional-card overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary to-primary/80 flex items-center justify-center">
                <User className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">
                  {isValid(details.name) ? details.name : "Resume Details"}
                </CardTitle>
                <p className="text-muted-foreground text-sm">
                  Parsed Resume Information
                </p>
              </div>
            </div>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant="outline"
                onClick={onReparse}
                className="btn-professional"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reparse
              </Button>
            </motion.div>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-8">
          {/* ================= CONTACT ================= */}
          <motion.div variants={itemVariants} className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center space-x-2">
              <Mail className="h-5 w-5 text-primary" />
              <span>Contact Information</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isValid(details.email) && (
                <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{details.email}</span>
                </div>
              )}

              {isValid(details.phone) && (
                <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{details.phone}</span>
                </div>
              )}

              {isValid(details.location) && (
                <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{details.location}</span>
                </div>
              )}

              {isValid(details.linkedin) && (
                <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                  <Linkedin className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={details.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    LinkedIn Profile
                  </a>
                </div>
              )}

              {isValid(details.github) && (
                <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                  <Github className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={details.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    GitHub Profile
                  </a>
                </div>
              )}

              {isValid(details.portfolio) && (
                <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={details.portfolio}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    Portfolio
                  </a>
                </div>
              )}
            </div>
          </motion.div>

          {/* ================= SUMMARY ================= */}
          {isValid(details.summary) && (
            <motion.div variants={itemVariants} className="space-y-3">
              <h3 className="text-lg font-semibold">Professional Summary</h3>
              <div className="p-4 bg-muted/20 rounded-lg border-l-4 border-primary">
                <p className="text-sm leading-relaxed">
                  {details.summary}
                </p>
              </div>
            </motion.div>
          )}

          {/* ================= SKILLS ================= */}
          {(hasValidArray(details.technicalSkills, isValid) ||
            hasValidArray(details.softSkills, isValid) ||
            hasValidArray(details.skills, isValid)) && (
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <Code className="h-5 w-5 text-primary" />
                <span>Skills</span>
              </h3>

              <div className="space-y-4">
                {hasValidArray(details.technicalSkills, isValid) && (
                  <div>
                    <h4 className="font-medium mb-2 text-sm text-muted-foreground">
                      Technical Skills
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {details.technicalSkills!
                        .filter(isValid)
                        .map((skill, index) => (
                          <Badge
                            key={index}
                            variant="secondary"
                            className="bg-primary/10 text-primary border-primary/20"
                          >
                            {skill}
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}

                {hasValidArray(details.softSkills, isValid) && (
                  <div>
                    <h4 className="font-medium mb-2 text-sm text-muted-foreground">
                      Soft Skills
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {details.softSkills!
                        .filter(isValid)
                        .map((skill, index) => (
                          <Badge key={index} variant="outline">
                            {skill}
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}

                {hasValidArray(details.skills, isValid) &&
                  !hasValidArray(details.technicalSkills, isValid) &&
                  !hasValidArray(details.softSkills, isValid) && (
                    <div className="flex flex-wrap gap-2">
                      {details.skills!
                        .filter(isValid)
                        .map((skill, index) => (
                          <Badge key={index} variant="secondary">
                            {skill}
                          </Badge>
                        ))}
                    </div>
                  )}
              </div>
            </motion.div>
          )}

          {/* ================= EXPERIENCE ================= */}
          {hasValidArray(details.experience, exp =>
            isValid(exp.role) && isValid(exp.company)
          ) && (
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <Briefcase className="h-5 w-5 text-primary" />
                <span>Professional Experience</span>
              </h3>

              <div className="space-y-4">
                {details.experience!
                  .filter(
                    exp =>
                      isValid(exp.role) && isValid(exp.company)
                  )
                  .map((exp, index) => (
                    <div key={index} className="professional-card p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-lg">
                            {exp.role}
                          </h4>
                          <p className="text-primary font-medium">
                            {exp.company}
                          </p>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1 sm:mt-0">
                          {isValid(exp.dates) && <p>{exp.dates}</p>}
                          {isValid(exp.location) && (
                            <p>{exp.location}</p>
                          )}
                        </div>
                      </div>

                      {isValid(exp.description) && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {exp.description}
                        </p>
                      )}

                      {hasValidArray(exp.achievements, isValid) && (
                        <div className="mb-3">
                          <h5 className="font-medium text-sm mb-2">
                            Key Achievements:
                          </h5>
                          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                            {exp.achievements!
                              .filter(isValid)
                              .map((a, i) => (
                                <li key={i}>{a}</li>
                              ))}
                          </ul>
                        </div>
                      )}

                      {hasValidArray(exp.technologies, isValid) && (
                        <div className="flex flex-wrap gap-1">
                          {exp.technologies!
                            .filter(isValid)
                            .map((t, i) => (
                              <Badge
                                key={i}
                                variant="outline"
                                className="text-xs"
                              >
                                {t}
                              </Badge>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </motion.div>
          )}

          {/* ================= EDUCATION ================= */}
          {hasValidArray(details.education, edu =>
            isValid(edu.degree) && isValid(edu.institution)
          ) && (
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <GraduationCap className="h-5 w-5 text-primary" />
                <span>Education</span>
              </h3>

              <div className="space-y-3">
                {details.education!
                  .filter(
                    edu =>
                      isValid(edu.degree) &&
                      isValid(edu.institution)
                  )
                  .map((edu, index) => (
                    <div key={index} className="professional-card p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2">
                        <div>
                          <h4 className="font-semibold">
                            {edu.degree}
                          </h4>
                          <p className="text-primary">
                            {edu.institution}
                          </p>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1 sm:mt-0">
                          {isValid(edu.graduationYear) && (
                            <p>
                              Class of {edu.graduationYear}
                            </p>
                          )}
                          {isValid(edu.gpa) && (
                            <p>GPA: {edu.gpa}</p>
                          )}
                        </div>
                      </div>

                      {isValid(edu.honors) && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {edu.honors}
                        </p>
                      )}

                      {hasValidArray(
                        edu.relevantCourses,
                        isValid
                      ) && (
                        <div>
                          <h5 className="font-medium text-sm mb-1">
                            Relevant Courses:
                          </h5>
                          <div className="flex flex-wrap gap-1">
                            {edu.relevantCourses!
                              .filter(isValid)
                              .map((c, i) => (
                                <Badge
                                  key={i}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {c}
                                </Badge>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </motion.div>
          )}

          {/* ================= CERTIFICATIONS ================= */}
          {hasValidArray(details.certifications, cert =>
            isValid(cert.name) && isValid(cert.issuer)
          ) && (
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <Award className="h-5 w-5 text-primary" />
                <span>Certifications</span>
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {details.certifications!
                  .filter(
                    cert =>
                      isValid(cert.name) &&
                      isValid(cert.issuer)
                  )
                  .map((cert, index) => (
                    <div
                      key={index}
                      className="professional-card p-3"
                    >
                      <h4 className="font-semibold text-sm">
                        {cert.name}
                      </h4>
                      <p className="text-primary text-sm">
                        {cert.issuer}
                      </p>
                      {isValid(cert.date) && (
                        <p className="text-xs text-muted-foreground">
                          {cert.date}
                        </p>
                      )}
                      {isValid(cert.credentialId) && (
                        <p className="text-xs text-muted-foreground">
                          ID: {cert.credentialId}
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </motion.div>
          )}

          {/* ================= PROJECTS ================= */}
          {hasValidArray(details.projects, p => isValid(p.name)) && (
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <Code className="h-5 w-5 text-primary" />
                <span>Projects</span>
              </h3>

              <div className="space-y-3">
                {details.projects!
                  .filter(p => isValid(p.name))
                  .map((project, index) => (
                    <div
                      key={index}
                      className="professional-card p-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2">
                        <div>
                          <h4 className="font-semibold">
                            {project.name}
                          </h4>
                          {isValid(project.url) && (
                            <a
                              href={project.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary text-sm hover:underline"
                            >
                              View Project
                            </a>
                          )}
                        </div>
                        {isValid(project.duration) && (
                          <p className="text-sm text-muted-foreground">
                            {project.duration}
                          </p>
                        )}
                      </div>

                      {isValid(project.description) && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {project.description}
                        </p>
                      )}

                      {hasValidArray(
                        project.technologies,
                        isValid
                      ) && (
                        <div className="flex flex-wrap gap-1">
                          {project.technologies!
                            .filter(isValid)
                            .map((t, i) => (
                              <Badge
                                key={i}
                                variant="outline"
                                className="text-xs"
                              >
                                {t}
                              </Badge>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </motion.div>
          )}

          {/* ================= LANGUAGES ================= */}
          {hasValidArray(details.languages, isValid) && (
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <Languages className="h-5 w-5 text-primary" />
                <span>Languages</span>
              </h3>

              <div className="flex flex-wrap gap-2">
                {details.languages!
                  .filter(isValid)
                  .map((lang, index) => (
                    <Badge key={index} variant="secondary">
                      {lang}
                    </Badge>
                  ))}
              </div>
            </motion.div>
          )}

          {/* ================= INTERESTS ================= */}
          {hasValidArray(details.interests, isValid) && (
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <Heart className="h-5 w-5 text-primary" />
                <span>Interests</span>
              </h3>

              <div className="flex flex-wrap gap-2">
                {details.interests!
                  .filter(isValid)
                  .map((interest, index) => (
                    <Badge key={index} variant="outline">
                      {interest}
                    </Badge>
                  ))}
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ResumeCard;
